import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ResponseModel } from 'src/app/shared/models/ResponseModel';
import { SILENT_BACKGROUND } from 'src/app/shared/interceptors/http-context-tokens';
import { environment } from 'src/environments/environment';

import { INomAgreement } from '../../nomina/novedades-ausencias/interfaces/INomAgreement';
import { INomCutDate } from '../../nomina/novedades-ausencias/interfaces/INomCutDate';
import { IEmpleadoRetiro } from '../interfaces/IEmpleadoRetiro';
import { IEmpleadoActivoRetiro } from '../interfaces/IEmpleadoActivoRetiro';
import { IRetirarEmpleado } from '../interfaces/IRetirarEmpleado';
import { IResultadoRetiroMasivo } from '../interfaces/IResultadoRetiroMasivo';
import { IRetiroContextoResuelto } from '../interfaces/IRetiroContextoResuelto';
import {
    IEstadoLiquidacion,
    ILiquidacionDetalle,
    ILiquidacionHistorico,
    IReporteLiquidacionEmpleado,
    ILiquidacionPendiente,
    ILiquidarRetiro,
    IPrechequeoLiquidacion,
    ISolicitudOperaciones
} from '../interfaces/IPrechequeoLiquidacion';

/**
 * Servicio de la Administración de retiros (F3).
 *
 * Cablea los endpoints YA EXISTENTES del controlador PaysheetNovelty:
 *  - GET  agreements/{nit}                → convenios del cliente
 *  - GET  cut-dates/{nit}/{agreement}     → fechas de corte
 *
 * El retiro ya NO va por PaysheetNovelty: se registra vía POST GestionRetiros/retirar
 * (endpoint propio). También expone la carga masiva de retiros por Excel.
 */
@Injectable({ providedIn: 'root' })
export class GestionRetirosService {
    private http = inject(HttpClient);
    private apiUrl = environment.backUrlApi + 'PaysheetNovelty/';

    /**
     * Obtiene los convenios disponibles para el NIT de la empresa.
     * Mismo endpoint que consume la pantalla de Novedades.
     */
    getAgreements(nit: string): Observable<ResponseModel<INomAgreement[]>> {
        return this.http.get<ResponseModel<INomAgreement[]>>(`${this.apiUrl}agreements/${nit}`);
    }

    /**
     * Obtiene las fechas de corte para el NIT y convenio dados.
     * incomeType fijo en 1 (Regular) para el flujo de retiros.
     */
    getCutDates(nit: string, agreement: string): Observable<ResponseModel<INomCutDate[]>> {
        const params = new HttpParams().set('incomeType', '1');
        return this.http.get<ResponseModel<INomCutDate[]>>(
            `${this.apiUrl}cut-dates/${nit}/${agreement}`,
            { params }
        );
    }

    /**
     * Listado de empleados activos a retirar de la empresa (modal "Retirar empleado").
     * Fuente: SP MI_ser_sp_empleados_activos_retiro vía GET GestionRetiros/empleados-activos.
     * La regla de selección (contrato vigente / retirado últimos 2 meses / antigüedad
     * >= 10 meses) se resuelve en el SP con la fecha de hoy como referencia.
     * @param codEmp Filtra un empleado puntual. Opcional.
     */
    getEmpleadosActivos(
        nit: string,
        codEmp?: string
    ): Observable<ResponseModel<IEmpleadoActivoRetiro[]>> {
        let params = new HttpParams().set('nit', nit);
        if (codEmp) params = params.set('codEmp', codEmp);
        return this.http.get<ResponseModel<IEmpleadoActivoRetiro[]>>(
            `${this.baseApi}GestionRetiros/empleados-activos`,
            { params }
        );
    }

    /**
     * Registra el retiro de un empleado vía el nuevo endpoint de gestión de retiros.
     * Ya NO pasa por PaysheetNovelty/retirement: el retiro dejó de ser una novedad.
     * POST api/GestionRetiros/retirar
     * IMPORTANTE: las fallas de regla de negocio regresan con HTTP 200 y success=false;
     * el llamador debe verificar response.success.
     */
    retirarEmpleado(dto: IRetirarEmpleado): Observable<ResponseModel<unknown>> {
        return this.http.post<ResponseModel<unknown>>(`${this.baseApi}GestionRetiros/retirar`, dto);
    }

    /**
     * Descarga la plantilla Excel para la carga masiva de retiros.
     * GET api/GestionRetiros/plantilla-retiro-masivo?nit=
     */
    descargarPlantillaRetiro(nit: string): Observable<Blob> {
        const params = new HttpParams().set('nit', nit);
        return this.http.get(`${this.baseApi}GestionRetiros/plantilla-retiro-masivo`, {
            params,
            responseType: 'blob'
        });
    }

    /**
     * Procesa la carga masiva de retiros a partir de un archivo Excel (.xlsx).
     * POST api/GestionRetiros/procesar-retiro-masivo (multipart: Nit + Archivo).
     */
    procesarRetiroMasivo(
        nit: string,
        archivo: File
    ): Observable<ResponseModel<IResultadoRetiroMasivo>> {
        const formData = new FormData();
        formData.append('Nit', nit);
        formData.append('Archivo', archivo);
        return this.http.post<ResponseModel<IResultadoRetiroMasivo>>(
            `${this.baseApi}GestionRetiros/procesar-retiro-masivo`,
            formData
        );
    }

    // ─── Endpoints nuevos F3 (otros controladores) ────────────────────────────

    /** Base absoluta de la API (backUrlApi ya trae el sufijo /api/). */
    private baseApi = environment.backUrlApi;

    /**
     * Empleados candidatos a retiro para la empresa: próximos a cumplir 1 año
     * (antigüedad 10-12 meses) o retirados en el último mes.
     * GET api/GestionRetiros/empleados-para-retiro?nit=&agreement=&dateCut=
     */
    getEmpleadosParaRetiro(
        nit: string,
        agreement?: string,
        dateCut?: string
    ): Observable<ResponseModel<IEmpleadoRetiro[]>> {
        let params = new HttpParams().set('nit', nit);
        if (agreement) params = params.set('agreement', agreement);
        if (dateCut) params = params.set('dateCut', dateCut);
        return this.http.get<ResponseModel<IEmpleadoRetiro[]>>(
            `${this.baseApi}GestionRetiros/empleados-para-retiro`,
            { params }
        );
    }

    /**
     * Flag ind_aprueba_liquidacion_manual del cliente. Habilita las acciones
     * "Generar borrador" y "Aprobar paz y salvo".
     * GET api/ClientPayrollParams/aprueba-liquidacion-manual/{nit}
     */
    getApruebaLiquidacionManual(nit: string): Observable<ResponseModel<boolean>> {
        return this.http.get<ResponseModel<boolean>>(
            `${this.baseApi}ClientPayrollParams/aprueba-liquidacion-manual/${nit}`
        );
    }

    /**
     * Resuelve automáticamente el convenio y la fecha de corte aplicables a los empleados
     * indicados, para abrir el asistente de retiros sin pedirle al cliente que los elija.
     * Si los empleados pertenecen a convenios distintos, alguno no tiene convenio vigente,
     * o no hay corte disponible, la respuesta vuelve con success=false y un mensaje en el
     * lenguaje del cliente (sin vocabulario de nómina) para mostrar en un toast.
     * GET api/GestionRetiros/resolver-convenio-corte?nit=&empleados=
     */
    resolverConvenioCorte(
        nit: string,
        empleados: string[]
    ): Observable<ResponseModel<IRetiroContextoResuelto>> {
        const params = new HttpParams().set('nit', nit).set('empleados', empleados.join(','));
        return this.http.get<ResponseModel<IRetiroContextoResuelto>>(
            `${this.baseApi}GestionRetiros/resolver-convenio-corte`,
            { params }
        );
    }

    /**
     * Prechequeo de liquidación: valida el estado del corte y, por empleado, si es
     * liquidable (fuero de estabilidad, ya liquidado, sin retiro registrado).
     * GET api/GestionRetiros/prechequeo-liquidacion?nit=&agreement=&dateCut=&empleados=
     */
    prechequeoLiquidacion(
        nit: string,
        agreement: string,
        dateCut: string,
        empleados: string[],
        noTrabajo?: string[]
    ): Observable<ResponseModel<IPrechequeoLiquidacion>> {
        let params = new HttpParams()
            .set('nit', nit)
            .set('agreement', agreement)
            .set('dateCut', dateCut)
            .set('empleados', empleados.join(','));
        // Marcados "no trabajó": el API devuelve cuáles NO son elegibles (sin nómina
        // del corte pagada), para frenarlos en el paso de novedades.
        if (noTrabajo && noTrabajo.length > 0) {
            params = params.set('noTrabajo', noTrabajo.join(','));
        }

        return this.http.get<ResponseModel<IPrechequeoLiquidacion>>(
            `${this.baseApi}GestionRetiros/prechequeo-liquidacion`,
            { params }
        );
    }

    /**
     * Dispara la liquidación de los empleados seleccionados.
     * POST api/GestionRetiros/liquidar
     */
    liquidar(dto: ILiquidarRetiro): Observable<ResponseModel<string>> {
        return this.http.post<ResponseModel<string>>(`${this.baseApi}GestionRetiros/liquidar`, dto);
    }

    /**
     * Envía por correo a la Dirección de Operaciones la solicitud de retiro de los
     * empleados marcados "no trabajó" que no cobraron la nómina del corte (los que
     * el paso 2 bloquea). POST api/GestionRetiros/liquidacion-solicitar-operaciones
     */
    solicitarRetiroOperaciones(dto: ISolicitudOperaciones): Observable<ResponseModel<string>> {
        return this.http.post<ResponseModel<string>>(
            `${this.baseApi}GestionRetiros/liquidacion-solicitar-operaciones`,
            dto
        );
    }

    /**
     * Consulta el estado del pipeline de liquidación (polling).
     * GET api/GestionRetiros/estado-liquidacion?nit=&agreement=&dateCut=
     * Marcada como silenciosa: es polling y no debe abrir el modal de error ni
     * sacar al usuario de la pantalla.
     */
    estadoLiquidacion(
        nit: string,
        agreement: string,
        dateCut: string
    ): Observable<ResponseModel<IEstadoLiquidacion>> {
        const params = new HttpParams().set('nit', nit).set('agreement', agreement).set('dateCut', dateCut);

        return this.http.get<ResponseModel<IEstadoLiquidacion>>(
            `${this.baseApi}GestionRetiros/estado-liquidacion`,
            { params, context: new HttpContext().set(SILENT_BACKGROUND, true) }
        );
    }

    // ─── Bandeja de aprobación (F4) ────────────────────────────────────────────

    /**
     * Liquidaciones calculadas pero aún sin cerrar, pendientes de que el cliente
     * las revise y apruebe. Sin nit devuelve las de todos los NIT permitidos del
     * usuario autenticado.
     * GET api/GestionRetiros/liquidaciones-pendientes?nit=
     */
    /**
     * @param silencioso true en los sondeos automáticos: marca la petición como
     * background para que no dispare el loader global ni el modal de error.
     */
    liquidacionesPendientes(
        nit?: string,
        silencioso = false
    ): Observable<ResponseModel<ILiquidacionPendiente[]>> {
        let params = new HttpParams();
        if (nit) params = params.set('nit', nit);
        return this.http.get<ResponseModel<ILiquidacionPendiente[]>>(
            `${this.baseApi}GestionRetiros/liquidaciones-pendientes`,
            {
                params,
                context: silencioso
                    ? new HttpContext().set(SILENT_BACKGROUND, true)
                    : new HttpContext()
            }
        );
    }

    /**
     * Conceptos reales por empleado de una liquidación pendiente (SALARIO,
     * CESANTÍAS, PRIMA DE SERVICIOS, etc.).
     * GET api/GestionRetiros/liquidacion-detalle?nit=&processId=
     */
    /**
     * Reporte de Liquidación de Contrato (NOM701) del proceso: encabezado del
     * empleado, bases y conceptos. GET api/GestionRetiros/liquidacion-reporte
     */
    liquidacionReporte(nit: string, processId: number): Observable<ResponseModel<IReporteLiquidacionEmpleado[]>> {
        const params = new HttpParams().set('nit', nit).set('processId', processId);
        return this.http.get<ResponseModel<IReporteLiquidacionEmpleado[]>>(
            `${this.baseApi}GestionRetiros/liquidacion-reporte`,
            { params }
        );
    }

    /**
     * PDF SSRS de la Liquidación de Contrato de un empleado del proceso.
     * GET api/GestionRetiros/liquidacion-reporte-pdf (blob).
     */
    liquidacionReportePdf(nit: string, processId: number, empleado: string): Observable<Blob> {
        const params = new HttpParams()
            .set('nit', nit)
            .set('processId', processId)
            .set('empleado', empleado);
        return this.http.get(`${this.baseApi}GestionRetiros/liquidacion-reporte-pdf`, {
            params,
            responseType: 'blob'
        });
    }

    /**
     * Recalcula una liquidación pendiente tras agregarle novedades nuevas.
     * POST api/GestionRetiros/recalcular-liquidacion
     */
    recalcularLiquidacion(nit: string, processId: number): Observable<ResponseModel<string>> {
        return this.http.post<ResponseModel<string>>(
            `${this.baseApi}GestionRetiros/recalcular-liquidacion`,
            { nit, processId }
        );
    }

    /**
     * Rechaza una liquidación pendiente: la elimina del ERP y revierte los retiros.
     * POST api/GestionRetiros/rechazar-liquidacion
     */
    rechazarLiquidacion(
        nit: string,
        processId: number,
        empleados?: string[]
    ): Observable<ResponseModel<string>> {
        return this.http.post<ResponseModel<string>>(
            `${this.baseApi}GestionRetiros/rechazar-liquidacion`,
            // Sin empleados el API rechaza el lote completo, como siempre.
            { nit, processId, empleados: empleados ?? [] }
        );
    }

    /**
     * Histórico de liquidaciones cerradas del NIT (portal + directas en Novasoft).
     * GET .../liquidaciones-historico?nit&desde&hasta (yyyyMMdd, opcionales).
     */
    liquidacionesHistorico(
        nit: string,
        desde?: string,
        hasta?: string
    ): Observable<ResponseModel<ILiquidacionHistorico[]>> {
        let params = new HttpParams().set('nit', nit);
        if (desde) params = params.set('desde', desde);
        if (hasta) params = params.set('hasta', hasta);
        return this.http.get<ResponseModel<ILiquidacionHistorico[]>>(
            `${this.baseApi}GestionRetiros/liquidaciones-historico`,
            { params }
        );
    }

    /** Reporte de un empleado por (convenio, corte) — histórico, sin proceso. */
    liquidacionReporteEmpleado(
        nit: string,
        empleado: string,
        agreement: string,
        dateCut: string
    ): Observable<ResponseModel<IReporteLiquidacionEmpleado[]>> {
        const params = new HttpParams()
            .set('nit', nit)
            .set('empleado', empleado)
            .set('agreement', agreement)
            .set('dateCut', dateCut);
        return this.http.get<ResponseModel<IReporteLiquidacionEmpleado[]>>(
            `${this.baseApi}GestionRetiros/liquidacion-reporte-empleado`,
            { params }
        );
    }

    /** PDF de un empleado por (convenio, corte) — histórico. */
    liquidacionReportePdfEmpleado(
        nit: string,
        empleado: string,
        agreement: string,
        dateCut: string
    ): Observable<Blob> {
        const params = new HttpParams()
            .set('nit', nit)
            .set('empleado', empleado)
            .set('agreement', agreement)
            .set('dateCut', dateCut);
        return this.http.get(`${this.baseApi}GestionRetiros/liquidacion-reporte-pdf-empleado`, {
            params,
            responseType: 'blob'
        });
    }

    /** Reporte de varios procesos a la vez. GET .../liquidacion-reporte-lote */
    liquidacionReporteLote(
        nit: string,
        processIds: number[]
    ): Observable<ResponseModel<IReporteLiquidacionEmpleado[]>> {
        const params = new HttpParams().set('nit', nit).set('processIds', processIds.join(','));
        return this.http.get<ResponseModel<IReporteLiquidacionEmpleado[]>>(
            `${this.baseApi}GestionRetiros/liquidacion-reporte-lote`,
            { params }
        );
    }

    /** Un solo PDF con las liquidaciones de varios procesos. */
    liquidacionReportePdfLote(nit: string, processIds: number[]): Observable<Blob> {
        const params = new HttpParams().set('nit', nit).set('processIds', processIds.join(','));
        return this.http.get(`${this.baseApi}GestionRetiros/liquidacion-reporte-pdf-lote`, {
            params,
            responseType: 'blob'
        });
    }

    liquidacionDetalle(nit: string, processId: number): Observable<ResponseModel<ILiquidacionDetalle[]>> {
        const params = new HttpParams().set('nit', nit).set('processId', processId);
        return this.http.get<ResponseModel<ILiquidacionDetalle[]>>(
            `${this.baseApi}GestionRetiros/liquidacion-detalle`,
            { params }
        );
    }

    /**
     * Aprueba y encola el cierre de una liquidación pendiente. Irreversible:
     * aprueba y cierra en Novasoft.
     * POST api/GestionRetiros/aprobar-liquidacion
     */
    aprobarLiquidacion(
        nit: string,
        processId: number,
        empleados?: string[]
    ): Observable<ResponseModel<string>> {
        return this.http.post<ResponseModel<string>>(`${this.baseApi}GestionRetiros/aprobar-liquidacion`, {
            nit,
            processId,
            // Sin empleados el API cierra el lote completo. Con un subconjunto, cierra
            // solo a esos y los demás vuelven a la bandeja en un proceso propio.
            empleados: empleados ?? []
        });
    }
}
